import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useBusinessContext } from '../contexts/BusinessContext'
import { API_URL } from '../config'
import ImageModal from '../components/ImageModal'
import LeadsModal from '../components/LeadsModal'
import { useDateFormatter } from '../utils/dateUtils'
import { Mail, AlertTriangle, Send, Bell, Users } from 'lucide-react'
import axios from 'axios'
import './AdvisorDashboard.css'

const AdvisorDashboard = () => {
  const { user, loading: authLoading } = useAuth()
  const { selectBusiness, clearBusiness } = useBusinessContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [businesses, setBusinesses] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    email: '',
    business_name: ''
  })
  const [inviteError, setInviteError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showCreateBusinessModal, setShowCreateBusinessModal] = useState(false)
  const [createBusinessForm, setCreateBusinessForm] = useState({
    business_name: '',
    industry: ''
  })
  const [createBusinessError, setCreateBusinessError] = useState('')
  const [createBusinessSuccess, setCreateBusinessSuccess] = useState('')
  const [creatingBusiness, setCreatingBusiness] = useState(false)
  const [deletingBusinessId, setDeletingBusinessId] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [businessToDelete, setBusinessToDelete] = useState(null)
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [imageModalUrl, setImageModalUrl] = useState('')
  const { formatDateTime } = useDateFormatter()

  // Messages and Alerts state
  const [messages, setMessages] = useState([])
  const [alerts, setAlerts] = useState([])
  const [showNewMessageModal, setShowNewMessageModal] = useState(false)
  const [showNewAlertModal, setShowNewAlertModal] = useState(false)
  const [messageForm, setMessageForm] = useState({
    business_id: '',
    subject: '',
    topic_category: 'General Question',
    message: '',
    priority: 'normal'
  })
  const [alertForm, setAlertForm] = useState({
    business_id: '',
    title: '',
    message: '',
    alert_type: 'info'
  })
  const [sendingMessage, setSendingMessage] = useState(false)
  const [sendingAlert, setSendingAlert] = useState(false)
  const [messageError, setMessageError] = useState('')
  const [alertError, setAlertError] = useState('')
  const [messageSuccess, setMessageSuccess] = useState('')
  const [alertSuccess, setAlertSuccess] = useState('')
  const [showLeadsModal, setShowLeadsModal] = useState(false)
  const [newLeadsCount, setNewLeadsCount] = useState(0)

  useEffect(() => {
    // Wait for auth to finish loading before checking role
    if (authLoading) {
      return;
    }

    // If auth finished loading and user is not an advisor, redirect
    if (!user || user.role !== 'advisor') {
      navigate('/dashboard')
      return
    }

    fetchData()
    fetchNewLeadsCount()
  }, [user, authLoading, navigate, location])

  const fetchData = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const headers = { 'Authorization': `Bearer ${token}` }

      // Fetch businesses
      const businessesRes = await fetch(`${API_URL}/advisor/businesses`, { headers })

      if (businessesRes.ok) {
        const businessesData = await businessesRes.json()
        setBusinesses(businessesData.businesses || [])
      }

      // Fetch invitations
      const invitationsRes = await fetch(`${API_URL}/advisor/invitations`, { headers })

      if (invitationsRes.ok) {
        const invitationsData = await invitationsRes.json()
        setInvitations(invitationsData.invitations || [])
      }

      // Fetch messages (recent 10)
      const messagesRes = await axios.get(`${API_URL}/messages/`, { headers })
      if (messagesRes.data.messages) {
        setMessages(messagesRes.data.messages.slice(0, 10))
      }

      // Fetch alerts (recent 10)
      const alertsRes = await axios.get(`${API_URL}/alerts/`, { headers })
      if (alertsRes.data.alerts) {
        setAlerts(alertsRes.data.alerts.slice(0, 10))
      }
    } catch (error) {
      console.error('Error fetching advisor data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchNewLeadsCount = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/advisor/leads/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setNewLeadsCount(data.count || 0)
      }
    } catch (error) {
      console.error('Error fetching new leads count:', error)
    }
  }

  const handleBusinessClick = async (business) => {
    await selectBusiness(business.id)
    navigate('/dashboard')
  }

  const handleInviteSubmit = async (e) => {
    e.preventDefault()
    setInviteError('')
    setInviteSuccess('')
    setSubmitting(true)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/advisor/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inviteForm)
      })

      const data = await response.json()

      if (response.ok) {
        setInviteSuccess(`Invitation sent successfully to ${inviteForm.email}`)
        setInviteForm({ email: '', business_name: '' })
        setTimeout(() => {
          setShowInviteModal(false)
          setInviteSuccess('')
          fetchData()
        }, 2000)
      } else {
        setInviteError(data.error || 'Failed to send invitation')
      }
    } catch (error) {
      setInviteError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateBusinessSubmit = async (e) => {
    e.preventDefault()
    setCreateBusinessError('')
    setCreateBusinessSuccess('')
    setCreatingBusiness(true)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/advisor/create-own-business`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createBusinessForm)
      })

      const data = await response.json()

      if (response.ok) {
        setCreateBusinessSuccess(`Business "${createBusinessForm.business_name}" created successfully!`)
        setCreateBusinessForm({ business_name: '', industry: '' })
        setTimeout(() => {
          setShowCreateBusinessModal(false)
          setCreateBusinessSuccess('')
          fetchData()
        }, 2000)
      } else {
        setCreateBusinessError(data.error || 'Failed to create business')
      }
    } catch (error) {
      setCreateBusinessError('Network error. Please try again.')
    } finally {
      setCreatingBusiness(false)
    }
  }

  const handleDeleteClick = (business, isOwnBusiness, e) => {
    e.stopPropagation() // Prevent card click
    setBusinessToDelete({ ...business, isOwnBusiness })
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!businessToDelete) return

    setDeletingBusinessId(businessToDelete.id)

    try {
      const token = localStorage.getItem('token')
      const endpoint = businessToDelete.isOwnBusiness
        ? `/advisor/own-business/${businessToDelete.id}`
        : `/advisor/client-business/${businessToDelete.id}`

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (response.ok) {
        // Remove the business from the list
        setBusinesses(businesses.filter(b => b.id !== businessToDelete.id))
        setShowDeleteConfirm(false)
        setBusinessToDelete(null)
      } else {
        alert(data.error || 'Failed to delete business')
      }
    } catch (error) {
      console.error('Error deleting business:', error)
      alert('Network error. Please try again.')
    } finally {
      setDeletingBusinessId(null)
    }
  }

  const getInviteStatus = (invitation) => {
    if (invitation.accepted) {
      return <span className="status-badge status-accepted">Accepted</span>
    }

    const expiresAt = new Date(invitation.expires_at)
    const now = new Date()

    if (expiresAt < now) {
      return <span className="status-badge status-expired">Expired</span>
    }

    return <span className="status-badge status-pending">Pending</span>
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    setMessageError('')
    setMessageSuccess('')
    setSendingMessage(true)

    try {
      const token = localStorage.getItem('token')

      if (!messageForm.business_id) {
        setMessageError('Please select a business')
        setSendingMessage(false)
        return
      }

      const response = await axios.post(
        `${API_URL}/messages/`,
        {
          subject: messageForm.subject,
          topic_category: messageForm.topic_category,
          message: messageForm.message,
          priority: messageForm.priority
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Business-Id': messageForm.business_id
          }
        }
      )

      if (response.data) {
        setMessageSuccess('Message sent successfully!')
        setMessageForm({
          business_id: '',
          subject: '',
          topic_category: 'General Question',
          message: '',
          priority: 'normal'
        })
        setTimeout(() => {
          setShowNewMessageModal(false)
          setMessageSuccess('')
          fetchData()
        }, 1500)
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setMessageError(error.response?.data?.error || 'Failed to send message')
    } finally {
      setSendingMessage(false)
    }
  }

  const handleSendAlert = async (e) => {
    e.preventDefault()
    setAlertError('')
    setAlertSuccess('')
    setSendingAlert(true)

    try {
      const token = localStorage.getItem('token')

      if (!alertForm.business_id) {
        setAlertError('Please select a business')
        setSendingAlert(false)
        return
      }

      const response = await axios.post(
        `${API_URL}/alerts/`,
        {
          title: alertForm.title,
          message: alertForm.message,
          alert_type: alertForm.alert_type
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Business-Id': alertForm.business_id
          }
        }
      )

      if (response.data) {
        setAlertSuccess('Alert created successfully!')
        setAlertForm({
          business_id: '',
          title: '',
          message: '',
          alert_type: 'info'
        })
        setTimeout(() => {
          setShowNewAlertModal(false)
          setAlertSuccess('')
          fetchData()
        }, 1500)
      }
    } catch (error) {
      console.error('Error creating alert:', error)
      setAlertError(error.response?.data?.error || 'Failed to create alert')
    } finally {
      setSendingAlert(false)
    }
  }

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-gray-500',
      normal: 'bg-blue-500',
      high: 'bg-orange-500',
      urgent: 'bg-red-500'
    }
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${colors[priority] || colors.normal}`}>
        {priority}
      </span>
    )
  }

  const getAlertTypeBadge = (type) => {
    const colors = {
      info: 'bg-blue-500',
      warning: 'bg-yellow-500',
      urgent: 'bg-red-500'
    }
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${colors[type] || colors.info}`}>
        {type}
      </span>
    )
  }

  if (authLoading || loading) {
    return (
      <div className="advisor-dashboard">
        <div className="loading">Loading...</div>
      </div>
    )
  }

  // Separate businesses into own and client
  const ownBusinesses = businesses.filter(b => b.is_own_business)
  const clientBusinesses = businesses.filter(b => !b.is_own_business)

  return (
    <div className="advisor-dashboard p-4 sm:p-6">
      <div className="advisor-header flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Advisor Dashboard</h1>
        <div className="advisor-header-actions flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            className="btn-secondary"
            onClick={() => setShowNewMessageModal(true)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '44px' }}
          >
            <Send size={16} />
            <span className="hidden sm:inline">New </span>Message
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowLeadsModal(true)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', position: 'relative', minHeight: '44px' }}
          >
            <Users size={16} />
            <span className="hidden sm:inline">View </span>Leads
            {newLeadsCount > 0 && (
              <span className="badge-count">{newLeadsCount}</span>
            )}
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowNewAlertModal(true)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '44px' }}
          >
            <Bell size={16} />
            <span className="hidden sm:inline">New </span>Alert
          </button>
          <button
            className="btn-secondary settings-link"
            onClick={() => navigate('/advisor/settings/notifications')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '44px' }}
            title="Notification Settings"
          >
            <Bell size={16} />
            <span className="hidden sm:inline">Settings</span>
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowCreateBusinessModal(true)}
            style={{ minHeight: '44px' }}
          >
            <span className="hidden sm:inline">+ Add My Own </span>Business
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowInviteModal(true)}
            style={{ minHeight: '44px' }}
          >
            <span className="hidden sm:inline">+ </span>Invite Client
          </button>
        </div>
      </div>

      <div className="advisor-content">
        {/* My Own Businesses Section */}
        <section className="businesses-section own-businesses-section mb-6">
          <div className="section-header mb-4">
            <h2 className="text-xl sm:text-2xl font-bold">My Own Businesses ({ownBusinesses.length})</h2>
          </div>
          {ownBusinesses.length === 0 ? (
            <div className="empty-state p-6 bg-gray-50 rounded-lg text-center">
              <p>You haven't added any of your own businesses yet. Click "Add My Own Business" to get started.</p>
            </div>
          ) : (
            <div className="businesses-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ownBusinesses.map((business) => (
                <div
                  key={business.id}
                  className="business-card own-business-card"
                  onClick={() => handleBusinessClick(business)}
                >
                  <div className="business-card-header">
                    {business.logo_url && (
                      <img
                        src={business.logo_url}
                        alt={`${business.business_name} logo`}
                        style={{ width: '48px', height: '48px', objectFit: 'contain', marginBottom: '8px', cursor: 'pointer' }}
                        className="hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageModalUrl(business.logo_url);
                          setImageModalOpen(true);
                        }}
                        title="Click to view full size"
                      />
                    )}
                    <h3>{business.business_name || 'Unnamed Business'}</h3>
                    <button
                      className="delete-btn"
                      onClick={(e) => handleDeleteClick(business, true, e)}
                      disabled={deletingBusinessId === business.id}
                      title="Delete business"
                    >
                      {deletingBusinessId === business.id ? '...' : '×'}
                    </button>
                  </div>
                  <div className="business-card-body">
                    <p className="business-owner">
                      <strong>Owner:</strong> {business.owner_name}
                    </p>
                    <p className="business-email">{business.owner_email}</p>
                    {business.industry && (
                      <p className="business-industry">
                        <strong>Industry:</strong> {business.industry}
                      </p>
                    )}
                    {business.updated_at && (
                      <p className="business-updated">
                        Last updated: {new Date(business.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="business-card-footer">
                    <span className="link-text">View Dashboard →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Client Businesses Section */}
        <section className="businesses-section client-businesses-section mb-6">
          <div className="section-header mb-4">
            <h2 className="text-xl sm:text-2xl font-bold">Client Businesses ({clientBusinesses.length})</h2>
          </div>
          {clientBusinesses.length === 0 ? (
            <div className="empty-state p-6 bg-gray-50 rounded-lg text-center">
              <p>No client businesses yet. Invite a business owner to get started.</p>
            </div>
          ) : (
            <div className="businesses-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clientBusinesses.map((business) => (
                <div
                  key={business.id}
                  className="business-card client-business-card"
                  onClick={() => handleBusinessClick(business)}
                >
                  <div className="business-card-header">
                    {business.logo_url && (
                      <img
                        src={business.logo_url}
                        alt={`${business.business_name} logo`}
                        style={{ width: '48px', height: '48px', objectFit: 'contain', marginBottom: '8px', cursor: 'pointer' }}
                        className="hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageModalUrl(business.logo_url);
                          setImageModalOpen(true);
                        }}
                        title="Click to view full size"
                      />
                    )}
                    <h3>{business.business_name || 'Unnamed Business'}</h3>
                    <button
                      className="delete-btn disconnect-btn"
                      onClick={(e) => handleDeleteClick(business, false, e)}
                      disabled={deletingBusinessId === business.id}
                      title="Disconnect from client"
                    >
                      {deletingBusinessId === business.id ? '...' : '×'}
                    </button>
                  </div>
                  <div className="business-card-body">
                    <p className="business-owner">
                      <strong>Owner:</strong> {business.owner_name}
                    </p>
                    <p className="business-email">{business.owner_email}</p>
                    {business.industry && (
                      <p className="business-industry">
                        <strong>Industry:</strong> {business.industry}
                      </p>
                    )}
                    {business.updated_at && (
                      <p className="business-updated">
                        Last updated: {new Date(business.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="business-card-footer">
                    <span className="link-text">View Dashboard →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Messages Section */}
        <section className="messages-section" style={{ marginBottom: '2rem' }}>
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>Recent Messages ({messages.length})</h2>
            <button
              className="btn-secondary"
              onClick={() => {
                clearBusiness();
                navigate('/inbox');
              }}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              View All Messages
            </button>
          </div>
          {messages.length === 0 ? (
            <div className="empty-state">
              <Mail size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
              <p>No messages yet</p>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    clearBusiness();
                    navigate('/inbox');
                  }}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    backgroundColor: message.is_read ? 'white' : '#eff6ff',
                    transition: 'background-color 0.2s',
                    minHeight: '44px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = message.is_read ? 'white' : '#eff6ff'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {getPriorityBadge(message.priority)}
                      <span style={{ fontSize: '12px', color: '#6b7280', padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px' }}>
                        {message.topic_category}
                      </span>
                      {!message.is_read && (
                        <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600' }}>NEW</span>
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDateTime(message.created_at)}</span>
                  </div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px', color: '#111827' }}>{message.subject}</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', wordBreak: 'break-word' }}>{message.message.substring(0, 100)}...</p>
                  <div style={{ fontSize: '12px', color: '#6b7280', wordBreak: 'break-word' }}>
                    <span><strong>From:</strong> {message.sender_display} - {message.business_name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Alerts Section */}
        <section className="alerts-section" style={{ marginBottom: '2rem' }}>
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>Recent Alerts ({alerts.length})</h2>
          </div>
          {alerts.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
              <p>No alerts yet</p>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #e5e7eb',
                    minHeight: '44px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {getAlertTypeBadge(alert.alert_type)}
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDateTime(alert.created_at)}</span>
                  </div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px', color: '#111827' }}>{alert.title}</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', wordBreak: 'break-word' }}>{alert.message}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280', wordBreak: 'break-word' }}>
                    <span><strong>Created by:</strong> {alert.created_by_display}</span>
                    <span><strong>Business:</strong> {alert.business_name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="invitations-section mb-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Invitations ({invitations.length})</h2>
          {invitations.length === 0 ? (
            <div className="empty-state p-6 bg-gray-50 rounded-lg text-center">
              <p>No invitations sent yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Business Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Expires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{invitation.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{invitation.business_name || '—'}</td>
                      <td className="px-4 py-3 text-sm">{getInviteStatus(invitation)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{new Date(invitation.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{new Date(invitation.expires_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invite New Business Owner</h2>
              <button
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleInviteSubmit} className="invite-form">
              {inviteError && (
                <div className="alert alert-error">{inviteError}</div>
              )}
              {inviteSuccess && (
                <div className="alert alert-success">{inviteSuccess}</div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required
                  placeholder="business@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="business_name">Business Name (Optional)</label>
                <input
                  type="text"
                  id="business_name"
                  value={inviteForm.business_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, business_name: e.target.value })}
                  placeholder="Acme Corp"
                />
                <small>This will pre-fill the business name for the invitee</small>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateBusinessModal && (
        <div className="modal-overlay" onClick={() => setShowCreateBusinessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add My Own Business</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateBusinessModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateBusinessSubmit} className="invite-form">
              {createBusinessError && (
                <div className="alert alert-error">{createBusinessError}</div>
              )}
              {createBusinessSuccess && (
                <div className="alert alert-success">{createBusinessSuccess}</div>
              )}

              <div className="form-group">
                <label htmlFor="business_name">Business Name *</label>
                <input
                  type="text"
                  id="business_name"
                  value={createBusinessForm.business_name}
                  onChange={(e) => setCreateBusinessForm({ ...createBusinessForm, business_name: e.target.value })}
                  required
                  placeholder="My Business LLC"
                />
              </div>

              <div className="form-group">
                <label htmlFor="industry">Industry (Optional)</label>
                <input
                  type="text"
                  id="industry"
                  value={createBusinessForm.industry}
                  onChange={(e) => setCreateBusinessForm({ ...createBusinessForm, industry: e.target.value })}
                  placeholder="Technology, Healthcare, etc."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateBusinessModal(false)}
                  disabled={creatingBusiness}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creatingBusiness}
                >
                  {creatingBusiness ? 'Creating...' : 'Create Business'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && businessToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{businessToDelete.isOwnBusiness ? 'Delete Business' : 'Disconnect from Client'}</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                {businessToDelete.isOwnBusiness
                  ? `Are you sure you want to delete "${businessToDelete.business_name || 'this business'}"? This action cannot be undone and will delete all associated data.`
                  : `Are you sure you want to disconnect from "${businessToDelete.business_name || 'this business'}"? You will no longer have access to this client's business data.`
                }
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingBusinessId !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deletingBusinessId !== null}
              >
                {deletingBusinessId ? 'Processing...' : (businessToDelete.isOwnBusiness ? 'Delete' : 'Disconnect')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="modal-overlay" onClick={() => setShowNewMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2>Send New Message</h2>
              <button className="modal-close" onClick={() => setShowNewMessageModal(false)}>×</button>
            </div>
            <form onSubmit={handleSendMessage} className="invite-form">
              {messageError && <div className="alert alert-error">{messageError}</div>}
              {messageSuccess && <div className="alert alert-success">{messageSuccess}</div>}

              <div className="form-group">
                <label htmlFor="message_business_id">Business *</label>
                <select
                  id="message_business_id"
                  value={messageForm.business_id}
                  onChange={(e) => setMessageForm({ ...messageForm, business_id: e.target.value })}
                  required
                >
                  <option value="">Select a business...</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.business_name} - {business.owner_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message_subject">Subject *</label>
                <input
                  type="text"
                  id="message_subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  required
                  placeholder="Message subject"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message_topic">Topic Category *</label>
                <select
                  id="message_topic"
                  value={messageForm.topic_category}
                  onChange={(e) => setMessageForm({ ...messageForm, topic_category: e.target.value })}
                  required
                >
                  <option value="Valuation">Valuation</option>
                  <option value="Exit Planning">Exit Planning</option>
                  <option value="Financial Data">Financial Data</option>
                  <option value="Gap Analysis">Gap Analysis</option>
                  <option value="Tasks & Action Items">Tasks & Action Items</option>
                  <option value="Advisory Team">Advisory Team</option>
                  <option value="General Question">General Question</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message_priority">Priority *</label>
                <select
                  id="message_priority"
                  value={messageForm.priority}
                  onChange={(e) => setMessageForm({ ...messageForm, priority: e.target.value })}
                  required
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message_text">Message *</label>
                <textarea
                  id="message_text"
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                  rows="6"
                  placeholder="Type your message..."
                  style={{ width: '100%', resize: 'vertical', padding: '12px' }}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowNewMessageModal(false)}
                  disabled={sendingMessage}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={sendingMessage}>
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Alert Modal */}
      {showNewAlertModal && (
        <div className="modal-overlay" onClick={() => setShowNewAlertModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2>Create New Alert</h2>
              <button className="modal-close" onClick={() => setShowNewAlertModal(false)}>×</button>
            </div>
            <form onSubmit={handleSendAlert} className="invite-form">
              {alertError && <div className="alert alert-error">{alertError}</div>}
              {alertSuccess && <div className="alert alert-success">{alertSuccess}</div>}

              <div className="form-group">
                <label htmlFor="alert_business_id">Business *</label>
                <select
                  id="alert_business_id"
                  value={alertForm.business_id}
                  onChange={(e) => setAlertForm({ ...alertForm, business_id: e.target.value })}
                  required
                >
                  <option value="">Select a business...</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.business_name} - {business.owner_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="alert_title">Alert Title *</label>
                <input
                  type="text"
                  id="alert_title"
                  value={alertForm.title}
                  onChange={(e) => setAlertForm({ ...alertForm, title: e.target.value })}
                  required
                  placeholder="Alert title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="alert_type">Alert Type *</label>
                <select
                  id="alert_type"
                  value={alertForm.alert_type}
                  onChange={(e) => setAlertForm({ ...alertForm, alert_type: e.target.value })}
                  required
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="alert_message">Message *</label>
                <textarea
                  id="alert_message"
                  value={alertForm.message}
                  onChange={(e) => setAlertForm({ ...alertForm, message: e.target.value })}
                  required
                  rows="4"
                  placeholder="Alert message..."
                  style={{ width: '100%', resize: 'vertical', padding: '12px' }}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowNewAlertModal(false)}
                  disabled={sendingAlert}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={sendingAlert}>
                  {sendingAlert ? 'Creating...' : 'Create Alert'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Leads Modal */}
      <LeadsModal
        isOpen={showLeadsModal}
        onClose={() => {
          setShowLeadsModal(false)
          fetchNewLeadsCount()
        }}
      />

      {/* Image Modal for Logo Preview */}
      <ImageModal
        isOpen={imageModalOpen}
        imageUrl={imageModalUrl}
        altText="Business logo"
        onClose={() => setImageModalOpen(false)}
      />
    </div>
  )
}

export default AdvisorDashboard
